import { Capacitor } from '@capacitor/core';
import { applyServerRow } from '../data/userStore';
import { getAccessToken } from './supabaseClient';
import { API_URL } from '../config/api';

// Login Patreon cote client. Tout le poids est cote serveur : Patreon refuse les
// custom schemes en redirect URI, ne supporte pas PKCE et exige le client_secret
// a l'echange du code. Le client ne fait donc qu'ouvrir une URL, puis reclamer
// le resultat avec SON JWT.
//
// Le client n'ecrit JAMAIS la row `purchase`/`patreon` de son propre chef : il
// applique telle quelle celle que le serveur renvoie (applyServerRow), avec le
// updated_at serveur. C'est ce qui permet a un tombstone ulterieur de gagner.


// Permet de masquer le bouton sur un build donne (App Store, guideline 3.1.3(b) :
// aucun lien, aucune mention de prix, aucune incitation a s'abonner hors de l'app).
export function isPatreonEnabled() {
  return process.env.REACT_APP_PATREON_ENABLED !== 'false';
}

const isNative = () => Capacitor.isNativePlatform();

async function authed(path, { method = 'GET', body } = {}) {
  const token = await getAccessToken();
  if (!token) throw new Error('no_session');
  const res = await fetch(`${API_URL}${path}`, {
    method,
    headers: {
      Authorization: `Bearer ${token}`,
      ...(body ? { 'Content-Type': 'application/json' } : {}),
    },
    body: body ? JSON.stringify(body) : undefined,
  });
  if (!res.ok) {
    const err = new Error(`patreon ${path} HTTP ${res.status}`);
    err.status = res.status;
    throw err;
  }
  return res.json();
}

// Applique la row renvoyee par le serveur au miroir + IndexedDB. Absente quand
// le serveur n'a rien change (refresh saute par le rate limit des 24 h).
async function applyEntitlement(body) {
  if (body?.row) await applyServerRow(body.row);
  return body;
}

// Ouvre la page d'autorisation Patreon. Sur natif, Browser.open lance un
// SFSafariViewController (iOS) / Custom Tab (Android) : la session cookie
// Patreon de l'utilisateur y est disponible, contrairement a une WebView.
export async function startPatreonLink() {
  const { url } = await authed(`/api/auth/patreon/start?platform=${isNative() ? 'native' : 'web'}`);
  if (isNative()) {
    const { Browser } = await import('@capacitor/browser');
    await Browser.open({ url });
  } else {
    window.location.href = url;
  }
  return { opened: true };
}

// Reclame le resultat de l'autorisation. Le serveur lie le compte Patreon au
// user_id de CE JWT, pas a celui qui a ouvert le flow : un lien d'autorisation
// forge par un tiers ne peut donc pas detourner l'acces d'un vrai membre.
export function claimPatreon(nonce) {
  return authed('/api/auth/patreon/claim', { method: 'POST', body: { nonce } }).then(applyEntitlement);
}

// Revalidation au boot online. Doit tourner AVANT le pull de sync : sinon le
// tombstone arriverait apres le pull, et un membre parti garderait son acces une
// session de plus. Ne throw jamais : une panne reseau laisse courir la grace.
export async function refreshPatreonEntitlement() {
  try {
    return await authed('/api/auth/patreon/refresh', { method: 'POST' }).then(applyEntitlement);
  } catch (err) {
    // 503 = Patreon indisponible ou non configure. On ne revoque rien.
    if (err.status !== 503) console.warn('[patreon] refresh echoue:', err.message);
    return null;
  }
}

export function unlinkPatreon() {
  return authed('/api/auth/patreon/unlink', { method: 'POST' }).then(applyEntitlement);
}

export function getPatreonStatus() {
  return authed('/api/auth/patreon/status');
}

// ── Retour dans l'app apres l'autorisation ────────────────────────────────

// Web : le serveur redirige sur `/?patreon=<nonce>`. On lit, puis on nettoie
// l'URL (le nonce est a usage unique, mais il n'a rien a faire dans l'historique).
export function readWebReturn(location = window.location) {
  const params = new URLSearchParams(location.search);
  const nonce = params.get('patreon');
  const error = params.get('patreon_error');
  if (!nonce && !error) return null;

  params.delete('patreon');
  params.delete('patreon_error');
  const search = params.toString();
  window.history.replaceState({}, '', `${location.pathname}${search ? `?${search}` : ''}${location.hash}`);
  return { nonce, error };
}

// Natif : le serveur sert une page interstitielle qui saute vers
// `upscale://patreon?patreon=<nonce>`. Une 302 directe vers un custom scheme
// depuis un SFSafariViewController est bloquee de facon inconsistante selon les
// versions d'iOS.
export function parseNativeReturn(url) {
  let parsed;
  try {
    parsed = new URL(url);
  } catch {
    return null;
  }
  if (parsed.host !== 'patreon' && parsed.pathname.replace(/\//g, '') !== 'patreon') return null;
  const params = parsed.searchParams;
  return { nonce: params.get('patreon'), error: params.get('patreon_error') };
}
